import React, { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useRouteMatch } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Button, Container } from '@mui/material';

import OfferBreadcrumbs from '~/components/Common/Elements/OfferBreadcrumbs';
import PageHeader from '~/components/Common/Elements/PageHeader';
import ErrorDisplay from '~/components/Common/ErrorDisplay';
import RoomTypesGroupsList from '~/components/Common/RoomTypesGroupsList';
import Spinner from '~/components/Common/Spinner';

import ReservationService from '~/services/ReservationService';
import VendorsService from '~/services/VendorsService';

type Props = { vendorName?: string };

export default function RoomTypesGroupsPageContainer(_: Props) {
  const {
    params: { id_vendor: vendorId, id_property: propertyId },
  } = useRouteMatch<{ id_vendor: string; id_property: string }>();

  const [property, setProperty] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [roomTypesGroups, setRoomTypesGroups] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);

    Promise.all([
      ReservationService.getProperty(propertyId),
      ReservationService.getRoomTypesGroups(propertyId),
      VendorsService.getVendorById(vendorId),
    ])
      .then(([property, roomTypesGroups, vendor]) => {
        setProperty(property.result);
        setRoomTypesGroups(roomTypesGroups.result);
        setVendor(vendor.result);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [propertyId]);

  if (isLoading) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorDisplay message={error} />;
  }

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{property?.name || propertyId} | Room Groups</title>
      </Helmet>

      <OfferBreadcrumbs
        vendor={{ label: vendor?.vendor_name || vendor?.name || 'Vendor', href: `/vendors/${vendorId}` }}
        property={{ label: property?.name || 'Property', href: `/vendors/${vendorId}/properties/${propertyId}` }}
        currentPage="Room groups"
      />
      <PageHeader title="Room Groups" />

      <RoomTypesGroupsList roomTypesGroups={roomTypesGroups} />

      <Button
        component={Link}
        variant="contained"
        to={`/vendors/${vendorId}/properties/${propertyId}/edit-room-types-groups`}
        sx={{ mt: 2 }}
      >
        Edit
      </Button>
    </Container>
  );
}
